import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  memo,
} from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { format } from "date-fns";
import { enGB } from "date-fns/locale";
import { axiosReqConfig } from "../functions/axiosRequest";
import { API_IP, STRIPE_KEY } from "../global_variables";
import PaymentModal from "./AccountComponents/PaymentModal";
import EtransferModal from "./AccountComponents/EtransferModal";
import MethodChoiceModal from "./AccountComponents/MethodChoiceModal";
import RatingModal from "./RatingModal";
import "./BookingHistory.css";

/* ─────────────────────────────────────────────────────────────
   Inline SVG icons — consistent stroke 1.75, currentColor
   ───────────────────────────────────────────────────────────── */
const Icon = ({ name, size = 18, className = "" }) => {
  const props = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.75,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className,
    "aria-hidden": "true",
  };
  switch (name) {
    case "calendar":
      return (
        <svg {...props}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4M8 3v4M3 10h18" />
        </svg>
      );
    case "user":
      return (
        <svg {...props}>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
        </svg>
      );
    case "stethoscope":
      return (
        <svg {...props}>
          <path d="M6 3v6a4 4 0 0 0 8 0V3" />
          <path d="M10 13v3a5 5 0 0 0 10 0v-3" />
          <circle cx="20" cy="10" r="2" />
        </svg>
      );
    case "mapPin":
      return (
        <svg {...props}>
          <path d="M12 22s-7-7.5-7-13a7 7 0 1 1 14 0c0 5.5-7 13-7 13Z" />
          <circle cx="12" cy="9" r="2.5" />
        </svg>
      );
    case "clock":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case "check":
      return (
        <svg {...props}>
          <path d="M5 12l5 5L20 7" />
        </svg>
      );
    case "checkCircle":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M8 12l3 3 5-6" />
        </svg>
      );
    case "xCircle":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M9 9l6 6M15 9l-6 6" />
        </svg>
      );
    case "hourglass":
      return (
        <svg {...props}>
          <path d="M6 3h12M6 21h12" />
          <path d="M7 3c0 4 4 5 5 9 1-4 5-5 5-9" />
          <path d="M7 21c0-4 4-5 5-9 1 4 5 5 5 9" />
        </svg>
      );
    case "card":
      return (
        <svg {...props}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 10h18M7 15h4" />
        </svg>
      );
    case "starFilled":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
          className={className}
        >
          <path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1L12 2z" />
        </svg>
      );
    case "chevronLeft":
      return (
        <svg {...props}>
          <path d="M15 6l-6 6 6 6" />
        </svg>
      );
    case "chevronRight":
      return (
        <svg {...props}>
          <path d="M9 6l6 6-6 6" />
        </svg>
      );
    case "chevronDown":
      return (
        <svg {...props}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    case "inbox":
      return (
        <svg {...props}>
          <path d="M3 13l3-7h12l3 7v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6Z" />
          <path d="M3 13h5l2 3h4l2-3h5" />
        </svg>
      );
    default:
      return null;
  }
};

/* ─────────────────────────────────────────────────────────────
   Custom horizontal stepper — drops MUI dependency
   ───────────────────────────────────────────────────────────── */
const STEPS = [
  { label: "Booking", icon: "calendar" },
  { label: "Provider", icon: "user" },
  { label: "Payment", icon: "card" },
  { label: "Completed", icon: "checkCircle" },
];

const Stepper = memo(function Stepper({ activeStep, status }) {
  return (
    <div
      className={`BS-stepper BS-stepper--${status}`}
      role="list"
      aria-label="Booking progress"
    >
      {STEPS.map((s, i) => {
        const state =
          i < activeStep ? "done" : i === activeStep ? "active" : "todo";
        return (
          <React.Fragment key={s.label}>
            <div
              className={`BS-step BS-step--${state}`}
              role="listitem"
              aria-current={state === "active" ? "step" : undefined}
            >
              <div className="BS-step-dot" aria-hidden="true">
                {state === "done" ? (
                  <Icon name="check" size={16} />
                ) : (
                  <Icon name={s.icon} size={16} />
                )}
              </div>
              <div className="BS-step-label">{s.label}</div>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={`BS-step-line ${
                  i < activeStep ? "BS-step-line--done" : ""
                }`}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
});

/* ─────────────────────────────────────────────────────────────
   Date helpers
   ───────────────────────────────────────────────────────────── */
function formatDateWithDay(dateString) {
  const d = new Date(dateString);
  return `${format(d, "EEE", { locale: enGB })} ${format(d, "d MMM yyyy", {
    locale: enGB,
  })}`;
}

function formatLongDate(dateString) {
  const d = new Date(dateString);
  return format(d, "EEEE, d MMMM yyyy", { locale: enGB });
}

/* ─────────────────────────────────────────────────────────────
   Status derivation
   ───────────────────────────────────────────────────────────── */
function deriveStatus(b) {
  if (b.completed) return "completed";
  if (b.acceptance === false) return "declined";
  if (b.paid) return "processing";
  if (b.acceptance === true) return "accepted";
  return "pending";
}

function deriveStep(b) {
  if (b.completed) return 4;
  if (!b.provider_confirmation && !b.paid) return 1;
  if (!b.paid) return 2;
  return 3;
}

const STATUS_LABEL = {
  pending: { label: "Awaiting Provider", icon: "hourglass" },
  accepted: { label: "Provider Accepted", icon: "checkCircle" },
  declined: { label: "Provider Declined", icon: "xCircle" },
  processing: { label: "Service In Progress", icon: "clock" },
  completed: { label: "Completed", icon: "check" },
};

/* Sort priority used by the "All" filter.
   Lower number = higher in the list. */
const STATUS_PRIORITY = {
  accepted: 1,    // needs payment — most urgent
  processing: 2,  // paid, waiting for completion
  pending: 3,     // waiting for provider confirmation
  declined: 4,    // dead-end, low priority
  completed: 5,   // archived
};

/* Filter definitions */
const FILTERS = [
  { id: "all", label: "All", match: () => true, icon: "inbox" },
  {
    id: "pay",
    label: "Pay Now",
    match: (b) => deriveStatus(b) === "accepted",
    icon: "card",
  },
  {
    id: "progress",
    label: "In Progress",
    match: (b) => deriveStatus(b) === "processing",
    icon: "clock",
  },
  {
    id: "awaiting",
    label: "Awaiting Provider",
    match: (b) => deriveStatus(b) === "pending",
    icon: "hourglass",
  },
  {
    id: "declined",
    label: "Declined",
    match: (b) => deriveStatus(b) === "declined",
    icon: "xCircle",
  },
  {
    id: "completed",
    label: "Completed",
    match: (b) => deriveStatus(b) === "completed",
    icon: "checkCircle",
  },
];

/* ─────────────────────────────────────────────────────────────
   Booking Card
   ───────────────────────────────────────────────────────────── */
const BookingCard = memo(function BookingCard({
  booking,
  index,
  isUnrated,
  isLoading,
  onRequestPayment,
  onRate,
}) {
  const [expanded, setExpanded] = useState(false);

  const status = deriveStatus(booking);
  const step = deriveStep(booking);
  const statusMeta = STATUS_LABEL[status];

  const startDate = formatDateWithDay(booking.start_date);
  const memberName = `${booking.first_name} ${booking.last_name}`;

  const acceptedCount = booking.accepted_dates?.length ?? 0;
  const declinedCount =
    booking.acceptance !== null ? booking.declined_dates?.length ?? 0 : 0;

  return (
    <article
      className={`BS-card BS-card--${status}`}
      style={{ animationDelay: `${Math.min(index, 6) * 0.06}s` }}
    >
      {/* Header */}
      <header className="BS-card-header">
        <div className="BS-header-left">
          <div className="BS-provider-icon-wrapper" aria-hidden="true">
            <Icon name="stethoscope" size={22} className="BS-provider-icon-svg" />
          </div>
          <div className="BS-header-text">
            <h3 className="BS-provider-name">{booking.provider_name}</h3>
            <div className="BS-service-row">
              <Icon name="user" size={13} />
              <span>{memberName}</span>
              <span className="BS-dot-sep">·</span>
              <span>{booking.care_service_name}</span>
            </div>
          </div>
        </div>
        <div className="BS-header-right">
          <span className={`BS-status-pill BS-status-pill--${status}`}>
            <Icon name={statusMeta.icon} size={13} />
            {statusMeta.label}
          </span>
          <span className="BS-booking-id-chip" title={`Booking #${booking.booking_id}`}>
            #{booking.booking_id}
          </span>
        </div>
      </header>

      {/* Body */}
      <div className="BS-card-body">
        <div className="BS-info-block">
          <div className="BS-info-row">
            <div className="BS-info-icon" aria-hidden="true">
              <Icon name="calendar" size={16} />
            </div>
            <div>
              <span className="BS-info-label">Start date</span>
              <h4 className="BS-info-value">{startDate}</h4>
            </div>
          </div>
          <div className="BS-info-row">
            <div className="BS-info-icon" aria-hidden="true">
              <Icon name="mapPin" size={16} />
            </div>
            <div>
              <span className="BS-info-label">Location</span>
              <span className="BS-info-sub">Victoria, BC</span>
            </div>
          </div>
        </div>

        <div className="BS-stepper-block">
          <Stepper activeStep={step} status={status} />
        </div>
      </div>

      {/* Footer */}
      <footer className="BS-card-footer">
        <button
          onClick={() => setExpanded((e) => !e)}
          className="BS-action-btn BS-btn-outline"
          aria-expanded={expanded}
          aria-controls={`BS-expand-${booking.booking_id}`}
        >
          {expanded ? "Hide schedule" : "View schedule"}
          {(acceptedCount > 0 || declinedCount > 0) && (
            <span className="BS-pill-count">
              {acceptedCount + declinedCount}
            </span>
          )}
          <Icon
            name="chevronDown"
            size={14}
            className={expanded ? "BS-chev-up" : ""}
          />
        </button>

        <div className="BS-footer-right">
          {step === 1 && (
            <button className="BS-action-btn BS-btn-disabled" disabled>
              <Icon name="hourglass" size={14} />
              {booking.acceptance === null
                ? "Waiting for provider"
                : "Provider declined"}
            </button>
          )}
          {step === 2 && (
            <button
              onClick={() => onRequestPayment(booking)}
              className="BS-action-btn BS-btn-pay"
              disabled={isLoading}
              aria-busy={isLoading || undefined}
            >
              {isLoading ? (
                <>
                  <span className="BS-btn-spinner" aria-hidden="true" />
                  Processing…
                </>
              ) : (
                <>
                  <Icon name="card" size={14} />
                  Pay to confirm
                </>
              )}
            </button>
          )}
          {step === 4 && isUnrated && (
            <button
              onClick={() => onRate(booking)}
              className="BS-action-btn BS-btn-primary"
            >
              <Icon name="starFilled" size={14} />
              Rate provider
            </button>
          )}
        </div>
      </footer>

      {/* Expanded schedule */}
      {expanded && (
        <div
          className="BS-card-expand"
          id={`BS-expand-${booking.booking_id}`}
          role="region"
          aria-label={`Schedule for booking ${booking.booking_id}`}
        >
          <div className="BS-expand-grid">
            <section className="BS-expand-section">
              <h5 className="BS-expand-title">
                <Icon name="checkCircle" size={14} className="BS-text-green-icon" />
                Accepted dates
              </h5>
              {acceptedCount === 0 ? (
                <p className="BS-expand-empty">No dates accepted yet.</p>
              ) : (
                <ul className="BS-expand-list">
                  {booking.accepted_dates.map((date, i) => (
                    <li key={`acc-${i}`} className="BS-expand-row">
                      <Icon name="checkCircle" size={15} />
                      <span className="BS-expand-date">
                        {formatLongDate(date)}
                      </span>
                      <span className="BS-expand-time">
                        {booking.accepted_start_times[i]} –{" "}
                        {booking.accepted_end_times[i]}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className="BS-expand-section">
              <h5 className="BS-expand-title">
                <Icon name="xCircle" size={14} className="BS-text-red-icon" />
                Declined dates
              </h5>
              {declinedCount === 0 ? (
                <p className="BS-expand-empty">No declined dates.</p>
              ) : (
                <ul className="BS-expand-list">
                  {booking.declined_dates.map((date, i) => (
                    <li key={`dec-${i}`} className="BS-expand-row">
                      <Icon name="xCircle" size={15} />
                      <span className="BS-expand-date">
                        {formatLongDate(date)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      )}
    </article>
  );
});

/* ─────────────────────────────────────────────────────────────
   Smart pagination with ellipsis
   ───────────────────────────────────────────────────────────── */
function paginationRange(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const range = [1];
  if (current > 3) range.push("…");
  for (
    let i = Math.max(2, current - 1);
    i <= Math.min(total - 1, current + 1);
    i++
  ) {
    range.push(i);
  }
  if (current < total - 2) range.push("…");
  range.push(total);
  return range;
}

const Pagination = memo(function Pagination({
  currentPage,
  totalPages,
  onChange,
  className = "",
}) {
  const range = useMemo(
    () => paginationRange(currentPage, totalPages),
    [currentPage, totalPages]
  );

  if (totalPages <= 1) return null;

  return (
    <nav
      className={`BS-pagination ${className}`}
      aria-label="Pagination"
    >
      <button
        onClick={() => onChange(Math.max(currentPage - 1, 1))}
        disabled={currentPage === 1}
        className="BS-page-btn"
        aria-label="Previous page"
      >
        <Icon name="chevronLeft" size={14} />
      </button>
      {range.map((n, idx) =>
        n === "…" ? (
          <span key={`ell-${idx}`} className="BS-page-ellipsis" aria-hidden="true">
            …
          </span>
        ) : (
          <button
            key={n}
            onClick={() => onChange(n)}
            className={`BS-page-btn ${
              currentPage === n ? "BS-page-active" : ""
            }`}
            aria-label={`Page ${n}`}
            aria-current={currentPage === n ? "page" : undefined}
          >
            {n}
          </button>
        )
      )}
      <button
        onClick={() => onChange(Math.min(currentPage + 1, totalPages))}
        disabled={currentPage === totalPages}
        className="BS-page-btn"
        aria-label="Next page"
      >
        <Icon name="chevronRight" size={14} />
      </button>
    </nav>
  );
});

/* ─────────────────────────────────────────────────────────────
   Main Screen
   ───────────────────────────────────────────────────────────── */
const stripePromise = loadStripe(STRIPE_KEY);
const ITEMS_PER_PAGE = 7;

const BookingScreen = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [bookings, setBookings] = useState([]);
  const [unratedBookings, setUnratedBookings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentVisible, setPaymentVisible] = useState(false);

  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [bookingToRate, setBookingToRate] = useState(null);

  const [clientSecret, setClientSecret] = useState("");
  const [totalPrice, setTotalPrice] = useState("");
  const [customerSession, setCustomerSession] = useState("");
  const [selectedBooking, setSelectedBooking] = useState({});
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [methodChoiceOpen, setMethodChoiceOpen] = useState(false);
  const [refresh, isRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingBookings, setIsFetchingBookings] = useState(true);

  const togglePayment = () => setPaymentVisible((v) => !v);
  const toggleRefresh = () => isRefresh((prev) => !prev);

  /* Per-filter counts + summary stats */
  const { filterCounts, inProcessCount, completedCount } = useMemo(() => {
    const counts = Object.fromEntries(FILTERS.map((f) => [f.id, 0]));
    let inProc = 0;
    let comp = 0;
    for (const b of bookings) {
      for (const f of FILTERS) {
        if (f.match(b)) counts[f.id]++;
      }
      if (b.completed) comp++;
      else inProc++;
    }
    return { filterCounts: counts, inProcessCount: inProc, completedCount: comp };
  }, [bookings]);

  /* Filtered + sorted list.
     "all" filter sorts by STATUS_PRIORITY so urgent items (pay-now) come first.
     Other filters keep insertion order (which is typically newest-first from API). */
  const filteredBookings = useMemo(() => {
    const filter = FILTERS.find((f) => f.id === activeFilter) || FILTERS[0];
    const list = bookings.filter(filter.match);
    if (activeFilter === "all") {
      return [...list].sort((a, b) => {
        const pa = STATUS_PRIORITY[deriveStatus(a)] ?? 99;
        const pb = STATUS_PRIORITY[deriveStatus(b)] ?? 99;
        if (pa !== pb) return pa - pb;
        // Within same priority, newest first by start_date
        return new Date(b.start_date) - new Date(a.start_date);
      });
    }
    return list;
  }, [bookings, activeFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredBookings.length / ITEMS_PER_PAGE)
  );
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentItems = filteredBookings.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  /* Data fetching */
  const fetchBookingInfo = useCallback(async () => {
    setIsFetchingBookings(true);
    try {
      const res = await axiosReqConfig(`${API_IP}/api/dashboard`, "get", {});
      const data = res?.ok ? res.data : null;
      setBookings(Array.isArray(data?.bookings) ? data.bookings : []);
    } catch (error) {
      console.error("Error fetching booking info:", error);
    } finally {
      setIsFetchingBookings(false);
    }
  }, [refresh]);

  const fetchUnratedBookings = useCallback(async () => {
    try {
      const res = await axiosReqConfig(`${API_IP}/api/rating`, "get", {});
      if (res?.ok && Array.isArray(res.data)) setUnratedBookings(res.data);
    } catch (error) {
      console.error("Error fetching unrated bookings:", error);
    }
  }, [refresh]);

  useEffect(() => {
    fetchBookingInfo();
    fetchUnratedBookings();
  }, [fetchBookingInfo, fetchUnratedBookings]);

  /* Payment flow */
  const requestPayment = useCallback((booking) => {
    setSelectedBooking(booking);
    setMethodChoiceOpen(true);
  }, []);

  const handleMethodChoose = async (method) => {
    setMethodChoiceOpen(false);
    if (method === "etransfer") {
      setPaymentMethod("etransfer");
      setPaymentVisible(true);
      return;
    }
    setIsLoading(true);
    try {
      const res = await axiosReqConfig(`${API_IP}/api/payment`, "post", {
        booking_id: selectedBooking.booking_id,
        service_id: selectedBooking.care_service_id,
        provider_id: selectedBooking.provider_id,
      });
      if (!res?.ok) {
        setIsLoading(false);
        return;
      }
      const { clientSecret, totalPrice, transactionId, customerSession } =
        res.data;
      setSelectedBooking((prev) => ({ ...prev, transactionId }));
      setTotalPrice(totalPrice);
      setCustomerSession(customerSession);
      setClientSecret(clientSecret);
      setPaymentMethod("card");
      setPaymentVisible(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenRating = useCallback((booking) => {
    setBookingToRate(booking);
    setRatingModalOpen(true);
  }, []);

  /* Reset page when filter changes & data shrinks below current */
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const switchFilter = (id) => {
    setActiveFilter(id);
    setCurrentPage(1);
  };

  return (
    <>
      <RatingModal
        isOpen={ratingModalOpen}
        onClose={() => setRatingModalOpen(false)}
        booking={bookingToRate}
        onSuccess={fetchUnratedBookings}
      />

      {isLoading && !paymentVisible && (
        <div className="BS-loading-overlay" role="status" aria-live="polite">
          <div className="BS-loading-modal">
            <img src={require("../images/loading_icon.gif")} alt="Loading" />
            <p>Preparing secure payment…</p>
          </div>
        </div>
      )}

      <MethodChoiceModal
        showModal={methodChoiceOpen}
        handleClose={() => setMethodChoiceOpen(false)}
        bookingId={selectedBooking?.booking_id}
        onChoose={handleMethodChoose}
      />

      {paymentVisible && paymentMethod === "card" && clientSecret && (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            customerSessionClientSecret: customerSession,
          }}
        >
          <PaymentModal
            showModal={paymentVisible}
            handleClose={togglePayment}
            clientSecret={clientSecret}
            customerSession={customerSession}
            totalPrice={totalPrice}
            selectedBooking={selectedBooking}
            toggleRefresh={toggleRefresh}
          />
        </Elements>
      )}

      {paymentVisible && paymentMethod === "etransfer" && (
        <EtransferModal
          showModal={paymentVisible}
          handleClose={togglePayment}
          selectedBooking={selectedBooking}
          toggleRefresh={toggleRefresh}
        />
      )}

      <div className="BS-container screen-container">
        {/* Page Header */}
        <header className="screen-header">
          <div className="screen-header-text">
            <h1 className="screen-title">Booking History</h1>
            <p className="screen-subtitle">
              Track and manage your care service requests
            </p>
          </div>
        </header>

        {/* Stats Bar */}
        {!isFetchingBookings && (
          <div className="BS-stats-bar" aria-label="Booking summary">
            <div className="BS-stat-item">
              <span className="BS-stat-value">{bookings.length}</span>
              <span className="BS-stat-label">Total bookings</span>
            </div>
            <div className="BS-stat-item BS-stat-item--process">
              <span className="BS-stat-value">{inProcessCount}</span>
              <span className="BS-stat-label">In process</span>
            </div>
            <div className="BS-stat-item BS-stat-item--success">
              <span className="BS-stat-value">{completedCount}</span>
              <span className="BS-stat-label">Completed</span>
            </div>
          </div>
        )}

        {/* Filters + Pagination */}
        <div className="BS-filters">
          <div
            className="BS-filters-left BS-filters-scroll"
            role="tablist"
            aria-label="Booking filter"
          >
            {FILTERS.map((f) => {
              const count = filterCounts[f.id] || 0;
              const active = activeFilter === f.id;
              return (
                <button
                  key={f.id}
                  role="tab"
                  aria-selected={active}
                  className={`BS-filter-btn BS-filter-btn--${f.id} ${
                    active ? "BS-filter-active" : ""
                  }`}
                  onClick={() => switchFilter(f.id)}
                >
                  <Icon name={f.icon} size={14} />
                  <span className="BS-filter-label">{f.label}</span>
                  <span className="BS-filter-count">{count}</span>
                </button>
              );
            })}
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onChange={setCurrentPage}
          />
        </div>

        {/* Card List */}
        <div className="BS-list-container">
          {isFetchingBookings ? (
            <div className="BS-loading-list" role="status" aria-live="polite">
              <img
                src={require("../images/loading_icon.gif")}
                alt=""
              />
              <span>Loading booking history…</span>
            </div>
          ) : currentItems.length > 0 ? (
            currentItems.map((booking, index) => {
              const isUnrated = unratedBookings.some(
                (ub) => ub.id === booking.booking_id
              );
              return (
                <BookingCard
                  key={booking.booking_id || booking.id}
                  booking={booking}
                  index={index}
                  isUnrated={isUnrated}
                  isLoading={isLoading}
                  onRequestPayment={requestPayment}
                  onRate={handleOpenRating}
                />
              );
            })
          ) : (
            <div className="BS-empty-state">
              <div className="BS-empty-icon" aria-hidden="true">
                <Icon name="inbox" size={28} />
              </div>
              <p>
                {activeFilter === "all"
                  ? "No bookings yet"
                  : `No bookings in "${
                      FILTERS.find((f) => f.id === activeFilter)?.label
                    }"`}
              </p>
              <span>
                {activeFilter === "all" ? (
                  "Your booking history will appear here once you make a request."
                ) : (
                  <>
                    Switch to{" "}
                    <button
                      type="button"
                      className="BS-empty-link"
                      onClick={() => switchFilter("all")}
                    >
                      All bookings
                    </button>{" "}
                    to see other requests.
                  </>
                )}
              </span>
            </div>
          )}
        </div>

        {/* Bottom Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onChange={setCurrentPage}
          className="BS-pagination-bottom"
        />
      </div>
    </>
  );
};

export default BookingScreen;
